import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { usePrograms, useSettings, resolveUrl } from "../lib/useCMS";
import { PageShell } from "../components/PageShell";
import { PageHero } from "../components/page/PageHero";
import { SEO } from "../components/page/SEO";
import { JsonLd } from "../components/page/JsonLd";
import { SITE_URL, SITE_NAME, absoluteUrl } from "../lib/site";
import { slugifyTitle } from "../lib/slug";
import { useInquiry, isInquiryCTA } from "../lib/InquiryContext";

export default function ProgramDetailPage() {
  const { slug } = useParams();
  const programs = usePrograms([]);
  const settings = useSettings({ square_booking_url: "" });
  const program = programs.find((p) => (p.slug || slugifyTitle(p.title)) === slug);
  const { openInquiry } = useInquiry();

  if (programs.length === 0) {
    return (
      <PageShell>
        <div className="min-h-[60vh] grid place-items-center text-zinc-400">Loading…</div>
      </PageShell>
    );
  }
  if (!program) return <Navigate to="/programs" replace />;

  const title = program.meta_title || `${program.title} | Built Different Hockey`;
  const desc = program.meta_description || program.description;
  const heroImg = program.hero_image_url || program.image_url;
  const ctaUrl = program.cta_url || settings.square_booking_url;
  const isInquiry = isInquiryCTA(program.cta_url);
  const work = program.what_youll_work_on || [];
  const outcomes = program.outcomes || [];

  return (
    <PageShell>
      <SEO title={title} description={desc} canonical={`/programs/${slug}`} image={heroImg} />
      <JsonLd
        id={`service-${slug}`}
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          "@id": `${SITE_URL}/programs/${slug}#service`,
          name: program.title,
          description: program.description || program.intro,
          provider: { "@id": `${SITE_URL}/#organization` },
          serviceType: program.tag || "Hockey training",
          areaServed: program.title === "Video Review" ? "Worldwide" : "United States",
          image: heroImg ? absoluteUrl(heroImg) : undefined,
          url: `${SITE_URL}/programs/${slug}`,
        }}
      />
      <JsonLd
        id={`breadcrumb-${slug}`}
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Programs", item: `${SITE_URL}/programs` },
            { "@type": "ListItem", position: 3, name: program.title, item: `${SITE_URL}/programs/${slug}` },
          ],
        }}
      />
      <PageHero
        overline={program.tag || "Program"}
        heading={program.title}
        subheading={program.intro || program.description}
        image={heroImg}
        eyebrowChildren={
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/programs" className="btn-ghost !py-2.5 !px-4 !text-xs">
              <ArrowLeft className="h-3.5 w-3.5" /> All Programs
            </Link>
            {ctaUrl && (
              isInquiry ? (
                <button type="button" onClick={() => openInquiry(program)} className="btn-primary" data-testid="program-detail-inquire-hero">
                  {program.cta_label || "Inquire Now"} <ArrowUpRight className="h-5 w-5" />
                </button>
              ) : (
                <a href={ctaUrl} target={ctaUrl.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="btn-primary">
                  {program.cta_label || "Book Now"} <ArrowUpRight className="h-5 w-5" />
                </a>
              )
            )}
          </div>
        }
      />

      <section className="bg-[#0a0a0a] py-16 md:py-24 border-b border-white/5">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bento-card p-6">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light">Who It's For</div>
            <div className="text-zinc-300 mt-3 text-sm leading-relaxed">{program.who_its_for || "Defensemen serious about long-term development."}</div>
          </div>
          <div className="bento-card p-6">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light">How It Runs</div>
            <div className="text-zinc-300 mt-3 text-sm leading-relaxed">{program.how_it_runs || "6:1 player-to-coach ratio. Purpose-driven reps. Real teaching, every session."}</div>
          </div>
        </div>
      </section>

      {(work.length > 0 || (program.features || []).length > 0) && (
        <section className="bg-[#0f0f0f] py-16 md:py-24 border-b border-white/5">
          <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
            <p className="overline">What You'll Work On</p>
            <h2 className="font-display text-3xl md:text-4xl uppercase mt-3 leading-tight mb-8">Every rep, with intent.</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {(work.length ? work : program.features || []).map((w, i) => (
                <motion.li key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: i * 0.05 }} className="flex items-start gap-3 border-b border-white/10 pb-3 text-zinc-200">
                  <CheckCircle2 className="h-4 w-4 text-brand-light shrink-0 mt-1" />
                  <span>{w}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {outcomes.length > 0 && (
        <section className="bg-[#0a0a0a] py-16 md:py-24 border-b border-white/5">
          <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
            <p className="overline">Outcomes</p>
            <h2 className="font-display text-3xl md:text-4xl uppercase mt-3 leading-tight mb-8">What you walk away with.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {outcomes.map((o, i) => (
                <div key={i} className="bento-card p-6 text-zinc-200">{o}</div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-brand py-16 md:py-20">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/80">Next Step</div>
            <div className="font-display text-3xl md:text-5xl uppercase mt-2 leading-[0.95] text-white">{program.detail_cta_label || program.cta_label || "Book Now."}</div>
          </div>
          {ctaUrl && (
            isInquiry ? (
              <button type="button" onClick={() => openInquiry(program)} className="inline-flex items-center gap-2 bg-white text-brand font-display uppercase tracking-wider px-6 py-4 text-base hover:bg-zinc-200 transition-colors" data-testid="program-detail-inquire-bottom">
                {program.cta_label || "Inquire"} <ArrowUpRight className="h-5 w-5" />
              </button>
            ) : (
              <a href={ctaUrl} target={ctaUrl.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-brand font-display uppercase tracking-wider px-6 py-4 text-base hover:bg-zinc-200 transition-colors">
                {program.cta_label || "Book"} <ArrowUpRight className="h-5 w-5" />
              </a>
            )
          )}
        </div>
      </section>
    </PageShell>
  );
}
